import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material'
import { MOBILE_EVENT_CODE } from '@mobileTypes'

import { EventFlowsEditor } from './EventFlowsEditor'
import type { EventFlowElementRef, EventFlowsForElementViewProps } from './EventFlowsForElementView'
import type { MobileEventFlow } from '@mobileTypes'

export interface EventFlowsForElementModalProps
  extends Omit<EventFlowsForElementViewProps, 'eventFlows' | 'elementRef'> {
  open: boolean
  onClose: () => void
  title?: string
  eventFlows: MobileEventFlow[]
  elementRef: EventFlowElementRef
  /** Form elements for button/swipe/field selection (when element is form element) */
  formElements?: any[]
  /** Called when event flows change (add/edit/delete flow or action). Receives the new full array. */
  onEventFlowsChange: (flows: MobileEventFlow[]) => void
}

export const EventFlowsForElementModal = ({
  open,
  onClose,
  title = 'Event flows for this element',
  eventFlows,
  elementRef,
  drawerItems = [],
  formElements = [],
  onEventFlowsChange,
}: EventFlowsForElementModalProps) => {
  const isDrawerItem = elementRef.type === 'drawer_item'
  const isButton = elementRef.type === 'button'
  const isSwipe = elementRef.type === 'swipe'
  const isField = elementRef.type === 'field'
  const isEmbeddedList = elementRef.type === 'embedded_list'
  const allowedEventCodes =
    isDrawerItem
      ? [MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS]
      : isButton
        ? [MOBILE_EVENT_CODE.ON_BUTTON_PRESS]
        : isSwipe
          ? [MOBILE_EVENT_CODE.ON_BUTTON_SWIPE]
          : isField
            ? [MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE]
            : isEmbeddedList
              ? [
                  MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE,
                  MOBILE_EVENT_CODE.ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU,
                ]
              : undefined
  const defaultValuesForNewFlow =
    isDrawerItem
      ? {
          eventCode: MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS as const,
          drawerItemInternalId: elementRef.drawerItemInternalId,
        }
      : isButton
        ? { eventCode: MOBILE_EVENT_CODE.ON_BUTTON_PRESS as const, elementInternalId: elementRef.elementInternalId }
        : isSwipe
          ? { eventCode: MOBILE_EVENT_CODE.ON_BUTTON_SWIPE as const, elementInternalId: elementRef.elementInternalId }
          : isField
            ? {
                eventCode: MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE as const,
                fieldInternalId: elementRef.fieldInternalId,
              }
            : isEmbeddedList
              ? { eventCode: MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE as const, elementId: elementRef.elementId }
              : undefined

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <EventFlowsEditor
          eventFlows={eventFlows}
          onEventFlowsChange={onEventFlowsChange}
          formElements={formElements}
          drawerItems={drawerItems}
          allowedEventCodes={allowedEventCodes}
          filterByElementRef={elementRef}
          defaultValuesForNewFlow={defaultValuesForNewFlow}
          showAddListenerButton={true}
          variables={[]}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
